import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Tailwind class merger. Use instead of raw template literals. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ---- Time helpers ----------------------------------------------------------
//
// Every timestamp the backend surfaces is a Unix second (UTC). The operator
// works in KST — the trading day, the plan cron, the journal all live in
// Asia/Seoul. Rendering any timestamp in the browser's local TZ would make
// "23:00 plan time" look wrong to an operator traveling abroad.
//
// Rule: call these helpers instead of `new Date().toLocale*` or
// `d.getHours()` directly. The helpers pin timezone to Asia/Seoul.

export const KST_TIME_ZONE = 'Asia/Seoul'

/** Date + time in KST, e.g. "2026. 4. 20. 오후 11:00:30". */
export function formatTime(unix?: number | null) {
  if (!unix) return '-'
  return new Date(unix * 1000).toLocaleString('ko-KR', { timeZone: KST_TIME_ZONE })
}

/** Date only in KST, YYYY-MM-DD. Stable format for URL params + API dates. */
export function formatDateKST(unix?: number | null) {
  if (!unix) return '-'
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: KST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(unix * 1000))
  const y = parts.find((p) => p.type === 'year')?.value ?? ''
  const m = parts.find((p) => p.type === 'month')?.value ?? ''
  const d = parts.find((p) => p.type === 'day')?.value ?? ''
  return `${y}-${m}-${d}`
}

/** Short HH:MM in KST. Used by the trading journal row. */
export function formatTimeShortKST(unix?: number | null) {
  if (!unix) return ''
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: KST_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(unix * 1000))
}

/** Short date+time in KST: "M/D HH:MM" (e.g. "4/26 22:34"). For stat cards
 *  where the ko-KR locale's "2026. 4. 26. 오후 10:34:30" is too noisy. */
export function formatDateTimeShortKST(unix?: number | null) {
  if (!unix) return '-'
  const d = new Date(unix * 1000)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: KST_TIME_ZONE,
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const m = parts.find((p) => p.type === 'month')?.value ?? ''
  const day = parts.find((p) => p.type === 'day')?.value ?? ''
  const h = parts.find((p) => p.type === 'hour')?.value ?? ''
  const min = parts.find((p) => p.type === 'minute')?.value ?? ''
  return `${m}/${day} ${h}:${min}`
}

/** Today's KST date as YYYY-MM-DD. Used as the default /plan date. */
export function todayKST(): string {
  return formatDateKST(Math.floor(Date.now() / 1000))
}

/**
 * formatRelativeKST — a 24h relative-ago label, fallback to absolute time
 * for anything older. Designed for /news timestamps where "1시간 21분 전"
 * is more useful than a full datetime but sub-minute precision is noise.
 *
 * Thresholds:
 *   < 1m      → "방금"
 *   < 1h      → "N분 전"
 *   < 24h     → "N시간 M분 전" (omit minutes when exact hour)
 *   ≥ 24h     → falls through to formatTime (absolute KST)
 *   future    → "N분 후" (rare; upstream clocks occasionally drift)
 */
export function formatRelativeKST(unix?: number | null): string {
  if (!unix) return '-'
  const nowSec = Math.floor(Date.now() / 1000)
  const deltaSec = nowSec - unix
  const absSec = Math.abs(deltaSec)

  // Past the 24h window: fall back to absolute KST datetime.
  if (absSec >= 24 * 3600) {
    return formatTime(unix)
  }

  const suffix = deltaSec < 0 ? ' 후' : ' 전'

  if (absSec < 60) return '방금'
  if (absSec < 3600) {
    const m = Math.floor(absSec / 60)
    return `${m}분${suffix}`
  }
  const h = Math.floor(absSec / 3600)
  const m = Math.floor((absSec % 3600) / 60)
  if (m === 0) return `${h}시간${suffix}`
  return `${h}시간 ${m}분${suffix}`
}
